import type { AdminLeadRow } from "@/components/admin/admin-leads-table";
import type { AdminLeadApi } from "@/lib/admin-api";
import { formatGbp } from "@/lib/tci/format";
import { getRegion, isRegionId } from "@/lib/tci/regions";

export function formatRegionLabel(regionId: string | null | undefined): string {
  const id = regionId?.trim().toLowerCase();
  if (!id || !isRegionId(id)) return "";
  return id.toUpperCase();
}

export function formatRegionName(regionId: string | null | undefined): string {
  const id = regionId?.trim().toLowerCase();
  if (!id || !isRegionId(id)) return "";
  return getRegion(id).title;
}

export function formatOrganisationLabel(
  organisationName: string | null | undefined,
): string {
  const name = organisationName?.trim();
  return name ? name : "";
}

export function formatSectorLabel(
  businessSector: string | null | undefined,
): string {
  const sector = businessSector?.trim();
  return sector ? sector : "";
}

export function formatCompactGbp(amount: number): string {
  const n = Number.isFinite(amount) ? amount : 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const millions = n / 1_000_000;
    const rounded =
      Math.abs(millions) >= 10
        ? millions.toFixed(0)
        : millions.toFixed(1).replace(/\.0$/, "");
    return `£${rounded}M`;
  }
  if (abs >= 1_000) {
    const thousands = n / 1_000;
    const rounded =
      Math.abs(thousands) >= 10
        ? thousands.toFixed(0)
        : thousands.toFixed(1).replace(/\.0$/, "");
    return `£${rounded}K`;
  }
  return formatGbp(n);
}

export function formatRelativeTime(iso: string, now = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";

  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return "Just now";
  if (diffMin === 1) return "1 min ago";
  if (diffMin < 60) return `${diffMin} mins ago`;
  if (diffHour === 1) return "1 hour ago";
  if (diffHour < 24) return `${diffHour} hours ago`;
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;

  return then
    .toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    .replace(/^([A-Za-z]{3}) /, "$1. ");
}

export function scoreToneFromScore(score: number): AdminLeadRow["scoreTone"] {
  return score >= 60 ? "critical" : "warning";
}

export function mapLeadApiToRow(lead: AdminLeadApi): AdminLeadRow {
  return {
    id: lead.id,
    sessionId: lead.sessionId,
    relativeTime: formatRelativeTime(lead.createdAt),
    email: lead.email,
    organisationName: formatOrganisationLabel(lead.organisationName),
    region: formatRegionLabel(lead.regionId),
    regionName: formatRegionName(lead.regionId),
    sector: formatSectorLabel(lead.businessSector),
    targetEbitda: formatGbp(lead.annualEbitda),
    totalDrag: formatGbp(lead.totalTrapped),
    tciScore: Math.round(lead.tciScore),
    scoreTone: scoreToneFromScore(lead.tciScore),
    breakdown: {
      labourWaste: formatGbp(lead.laborWaste),
      yieldPenalty: formatGbp(lead.yieldPenaltyWaste),
      untappedValue: formatGbp(lead.untappedValue),
      totalTrappedMargin: formatGbp(lead.totalTrapped),
    },
  };
}

const FUNNEL_STEP_LABELS: Record<string, string> = {
  intro: "Intro",
  workflow: "Workflow",
  calculator_1: "Labor Waste",
  calculator_2: "Yield Penalty",
  calculator_3: "Untapped Value",
  report_ready: "Report Ready",
  results: "Results",
  booking: "Booking",
};

export function formatFunnelStep(step: string | null | undefined): string {
  if (!step?.trim()) return "";
  return FUNNEL_STEP_LABELS[step] ?? step;
}

export type AdminVisitRow = {
  id: string;
  sessionId: string;
  relativeTime: string;
  lastSeenLabel: string;
  email: string;
  organisationName: string;
  region: string;
  sector: string;
  currentStep: string;
  status: string;
  workflow: string;
  tciScore: string;
};

export function mapVisitApiToRow(visit: {
  id: string;
  sessionId: string;
  email: string | null;
  organisationName?: string | null;
  regionId: string | null;
  businessSector: string | null;
  currentStep: string | null;
  status: string;
  workflowId: string | null;
  tciScore: number | null;
  createdAt: string;
  lastSeenAt: string;
}): AdminVisitRow {
  return {
    id: visit.id,
    sessionId: visit.sessionId,
    relativeTime: formatRelativeTime(visit.createdAt),
    lastSeenLabel: formatRelativeTime(visit.lastSeenAt),
    email: visit.email?.trim() || "",
    organisationName: formatOrganisationLabel(visit.organisationName),
    region: formatRegionLabel(visit.regionId),
    sector: formatSectorLabel(visit.businessSector),
    currentStep: formatFunnelStep(visit.currentStep),
    status: visit.status === "CONVERTED" ? "Converted" : "Active",
    workflow: visit.workflowId?.trim() || "",
    tciScore:
      visit.tciScore == null || !Number.isFinite(visit.tciScore)
        ? ""
        : String(Math.round(visit.tciScore)),
  };
}
